/**
 * Runtime environment flags derived from build-time globals.
 */
export interface RuntimeFlags {
  /** Whether this build targets the production Firebase cluster and hostname. */
  production: boolean;
  /** Whether Firebase emulator endpoints should be used. */
  emulator: boolean;
}

/**
 * Public URLs used by website links and release loading.
 */
export interface RuntimeUrls {
  /** HTTP scheme for the current environment. */
  securityPrefix: 'http://' | 'https://';
  /** Main website host. */
  baseURL: string;
  /** Fully qualified main website URL. */
  fullURL: string;
  /** Web opener subdomain. */
  openerSubdomain: 'open';
  /** Fully qualified URL used to launch MeshOS. */
  openerURL: string;
  /** Latest app release feed URL. */
  appReleaseFeed: string;
  /** Support email shown in errors. */
  supportEmail: string;
}

/**
 * Options for deriving public URLs.
 */
export type RuntimeUrlOptions = RuntimeFlags;

/**
 * Gets runtime flags, with test-safe fallbacks when Vite defines are absent.
 */
export function getRuntimeFlags(): RuntimeFlags {
  return {
    production: typeof __PRODUCTION__ === 'undefined' ? false : __PRODUCTION__,
    emulator: typeof __EMULATOR__ === 'undefined' ? false : __EMULATOR__,
  };
}

/**
 * Gets the Firebase config selected by Vite at build time.
 */
export function getFirebaseConfig(): Record<string, string> {
  if (typeof __FIREBASE_CONFIG__ === 'undefined') {
    throw new Error('Firebase config was not injected at build time.');
  }
  return __FIREBASE_CONFIG__;
}

/**
 * Gets the Slack shared secret injected at build time.
 */
export function getSlackSharedSecret(): string {
  if (typeof __SLACK_SHARED_SECRET__ === 'undefined') {
    return '';
  }
  return __SLACK_SHARED_SECRET__;
}

/**
 * Derives public URLs from runtime flags.
 */
export function deriveRuntimeUrls(options: RuntimeUrlOptions): RuntimeUrls {
  const securityPrefix = options.emulator ? 'http://' : 'https://';
  const baseURL = options.emulator
    ? 'localhost:3000'
    : options.production
      ? 'meshos.com'
      : 'meshos-dev.web.app';
  const fullURL = securityPrefix + baseURL;
  const openerSubdomain = 'open';

  return {
    securityPrefix,
    baseURL,
    fullURL,
    openerSubdomain,
    openerURL: securityPrefix + openerSubdomain + '.' + baseURL,
    appReleaseFeed: 'https://s3-us-west-1.amazonaws.com/us-west-1-meshos-downloads/releases/osx/beta/channel.xml',
    supportEmail: 'support@meshos.com',
  };
}

/**
 * Runtime URL constants for the active website.
 */
export const runtimeUrls = deriveRuntimeUrls(getRuntimeFlags());
